import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FlameIcon as Flame, HeartStraightIcon as HeartStraight, MegaphoneIcon as Megaphone, BellRingingIcon as BellRinging, CheckCircleIcon as CheckCircle, PlayCircleIcon as PlayCircle, BookOpenIcon as BookOpen, CrownIcon as Crown, MagnifyingGlassIcon as MagnifyingGlass, TruckIcon as Truck, StorefrontIcon as Storefront, WrenchIcon as Wrench, CheckIcon as Check, ShieldCheckIcon as ShieldCheck, UserCircleIcon as UserCircle, KeyIcon as Key, WarningIcon as Warning, EnvelopeSimpleIcon as EnvelopeSimple, PauseIcon as Pause } from "@phosphor-icons/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useProductTour from "@/hooks/useProductTour";
import { toast } from "sonner";
import { formatApiError, downloadAuthenticatedPdf } from "@/lib/utils-app";
import BreedAlertsCard from "@/components/BreedAlertsCard";
import BlockedUsersCard from "@/components/BlockedUsersCard";
import TwoFactorSettings from "@/components/TwoFactorSettings";
import KennelDefaultContractLinkCard from "@/components/KennelDefaultContractLinkCard";
import AvailabilityTab from "@/components/AvailabilityTab";
import { isNativeApp } from "@/lib/nativeBridge";
import { registerPushNotifications, haptic } from "@/lib/native";

const ROWS = [
  {
    key: "heat_reminder",
    title: "Heat cycle reminders",
    description:
      "Heads-up 5–9 days before an expected heat date on any of your female dogs.",
    Icon: Flame,
  },
  {
    key: "listing_expiring",
    title: "Listing expiry reminders",
    description:
      "Reminder 3 days before one of your breeding directory listings expires, so you can renew in one click.",
    Icon: HeartStraight,
  },
  {
    key: "broadcast",
    title: "Admin broadcasts",
    description:
      "Messages from event organizers to all registrants — weather updates, schedule changes, announcements.",
    Icon: Megaphone,
  },
];

const PUSH_ROWS = [
  {
    key: "push_messages",
    title: "Messages & replies",
    description: "Buzz when someone messages you or replies to an inquiry.",
    Icon: BellRinging,
  },
  {
    key: "push_watchlist",
    title: "Watchlist alerts",
    description: "New litters from kennels you watch and price drops on dogs you're following.",
    Icon: HeartStraight,
  },
  {
    key: "push_dogs_voice",
    title: "The Dog's Voice",
    description: "Reactions on your posts and the weekly photo prompt.",
    Icon: Flame,
  },
  {
    key: "push_reminders",
    title: "Reminders & announcements",
    description: "Checkout reminders, heat cycle & listing expiry alerts, event broadcasts.",
    Icon: Megaphone,
  },
];

// Mirror of PersonaPickerDialog's options. Kept in sync by hand since the
// copy is intentionally tailored per surface — here the phrasing is
// slightly terser ("for now" etc.) because the user already knows what
// the dialog did on their first visit.
const PERSONA_OPTIONS = [
  {
    key: "breeder",
    Icon: Crown,
    title: "I run a kennel",
    blurb: "Multiple dogs, pedigrees, litters, matings, transfers.",
  },
  {
    key: "owner",
    Icon: HeartStraight,
    title: "I'm an owner who loves my dog(s)",
    blurb: "One or two cherished dogs. Records, photos, community.",
  },
  {
    key: "buyer",
    Icon: MagnifyingGlass,
    title: "I'm hoping to find a dog",
    blurb: "Browsing kennels and litters to make an informed pick.",
  },
  {
    key: "service_provider",
    Icon: Wrench,
    title: "I'm a dog-service professional",
    blurb: "Trainer, groomer, walker, or sitter. Get bookings, paid via Stripe.",
  },
  {
    key: "storefront_seller",
    Icon: Storefront,
    title: "I sell dog products",
    blurb: "Food, gear, custom items. Open your own storefront with Stripe Tax.",
  },
  {
    key: "transporter",
    Icon: Truck,
    title: "I'm a licensed transporter",
    blurb: "USDA Class B ground transport. Bid on routes, get paid via Stripe.",
  },
];

// Tab definitions live as data so we can render the trigger row and the
// panel guards from the same source — keeps URL-hash deep linking and
// keyboard navigation in sync without per-tab branching.
const TABS = [
  { key: "profile",      label: "Profile",      Icon: UserCircle },
  { key: "availability", label: "Availability", Icon: Pause },
  { key: "notifications", label: "Notifications", Icon: BellRinging },
  { key: "security",     label: "Security",     Icon: ShieldCheck },
  { key: "legal",        label: "Legal",        Icon: BookOpen },
  { key: "danger",       label: "Privacy & account", Icon: ShieldCheck },
];

const VALID_TAB_KEYS = TABS.map((t) => t.key);

export default function Settings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [persona, setPersona] = useState(null);
  const [personaSaving, setPersonaSaving] = useState(false);
  const [digestOptIn, setDigestOptIn] = useState(null); // null = unknown / not admin
  const [digestSaving, setDigestSaving] = useState(false);
  const { replay } = useProductTour();
  const location = useLocation();
  const navigate = useNavigate();

  // Deep-linkable tab via URL hash. Falls back to "profile" so first
  // visits land on the highest-engagement panel.
  const initialTab = (() => {
    const h = (location.hash || "").replace("#", "");
    return VALID_TAB_KEYS.includes(h) ? h : "profile";
  })();
  const [tab, setTab] = useState(initialTab);

  // Keep URL hash and tab state in lockstep so a copied Settings URL
  // re-opens on the same panel. We use replace to avoid littering
  // browser history. The reverse direction (hash → tab) handles
  // browser back/forward and external links that change the hash
  // after the component is mounted.
  useEffect(() => {
    const hashTab = (location.hash || "").replace("#", "");
    if (tab && tab !== hashTab) {
      navigate(`#${tab}`, { replace: true });
    }
  }, [tab, location.hash, navigate]);

  useEffect(() => {
    const hashTab = (location.hash || "").replace("#", "");
    if (VALID_TAB_KEYS.includes(hashTab) && hashTab !== tab) {
      setTab(hashTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  useEffect(() => {
    api
      .get("/me/notification-preferences")
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({ heat_reminder: true, listing_expiring: true, broadcast: true }));
    api
      .get("/me/persona")
      .then((r) => setPersona(r.data?.persona || null))
      .catch(() => { /* non-fatal */ });
    // Admin-only: fetch the daily-digest opt-in state. 401/403 here just
    // means the user isn't an admin, in which case we keep digestOptIn
    // null and the toggle card never renders.
    api
      .get("/admin/automations/digest-opt-in")
      .then((r) => setDigestOptIn(!!r.data?.opted_in))
      .catch(() => setDigestOptIn(null));
  }, []);

  const toggleDigestOptIn = async (next) => {
    if (digestSaving) return;
    setDigestSaving(true);
    const prev = digestOptIn;
    setDigestOptIn(next); // optimistic
    try {
      await api.put("/admin/automations/digest-opt-in", { opted_in: next });
      toast.success(next ? "You'll receive the daily ops digest." : "Daily digest paused.");
    } catch (e) {
      setDigestOptIn(prev);
      toast.error("Could not update digest preference.");
    } finally {
      setDigestSaving(false);
    }
  };

  const choosePersona = async (next) => {
    if (next === persona || personaSaving) return;
    setPersonaSaving(true);
    const prev = persona;
    setPersona(next); // optimistic
    try {
      await api.post("/me/persona", { persona: next });
      // Keep Dashboard headline etc. in sync without a reload.
      window.dispatchEvent(new CustomEvent("persona-changed", { detail: { persona: next } }));
      toast.success("Updated — we'll tailor the experience from now on.");
    } catch (err) {
      setPersona(prev);
      toast.error(formatApiError(err));
    } finally {
      setPersonaSaving(false);
    }
  };

  const toggle = (key) => (next) => {
    setPrefs((cur) => ({ ...cur, [key]: next }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/notification-preferences", prefs);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      {/* Page headline */}
      <div className="mb-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Account
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Settings
        </h1>
        <p className="text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Tailor what brings you here, what shows up in your inbox, and how
          your account is locked down.
        </p>
      </div>

      {isNativeApp() && <NativeQuickActions />}

      <Tabs value={tab} onValueChange={setTab} className="space-y-8" data-testid="settings-tabs">
        {/* Tab triggers — gold underline on active, font-tabular keeps
            label widths predictable across the row. On mobile the row
            scrolls horizontally with a right-edge gradient fade so
            users can visibly see there's more (Legal + Danger zone). */}
        <div className="relative" data-testid="settings-tabs-container">
          <TabsList className="bg-transparent p-0 h-auto gap-1 border-b border-[#EAEAEA] rounded-none w-full justify-start overflow-x-auto scrollbar-none">
            {TABS.map(({ key, label, Icon }) => (
              <TabsTrigger
                key={key}
                value={key}
                data-testid={`settings-tab-${key}`}
                className="rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 -mb-px border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-ink text-[#6B6B6B] hover:text-ink transition-colors flex items-center gap-2 whitespace-nowrap shrink-0"
              >
                <Icon size={16} weight="duotone" />
                <span className="font-medium">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {/* Right-edge fade — only on viewports where the tab strip
              actually overflows (i.e. mobile). `sm:hidden` keeps the
              desktop layout pristine. */}
          <span
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 bottom-px w-12 bg-gradient-to-l from-white to-transparent sm:hidden"
          />
        </div>

        {/* PROFILE — persona + breed alerts (the "tell us about you" panel) */}
        <TabsContent value="profile" className="space-y-8 mt-0">
          <SectionCard
            icon={Crown}
            iconColor="text-gold"
            title="What brings you here?"
            blurb="Pick the one that fits best — it nudges a few bits of copy and defaults around the app. Change it anytime if your relationship to the registry evolves (e.g. an owner who decides to start breeding, or the other way around)."
            testId="persona-card"
          >
            <div className="grid gap-3">
              {PERSONA_OPTIONS.map(({ key, Icon, title, blurb }) => {
                const isSelected = persona === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => choosePersona(key)}
                    disabled={personaSaving}
                    data-testid={`persona-row-${key}`}
                    className={`text-left flex items-start gap-4 p-4 rounded-xl border transition-all duration-300 ${
                      isSelected
                        ? "border-ink bg-cream shadow-[0_0_0_3px_rgba(201,162,39,0.25)]"
                        : "border-[#EAEAEA] hover:border-ink/40 hover:bg-cream/50"
                    } ${personaSaving ? "opacity-60 cursor-wait" : ""}`}
                  >
                    <div
                      className={`p-2.5 rounded-lg flex-shrink-0 ring-1 ${
                        isSelected
                          ? "bg-gold/15 ring-gold/40"
                          : "bg-cream ring-[#E2DAC2]"
                      }`}
                    >
                      <Icon
                        size={20}
                        weight="duotone"
                        className={isSelected ? "text-gold" : "text-[#6B6B6B]"}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-spectral text-base tracking-tight text-ink">{title}</h3>
                      <p className="text-sm text-[#6B6B6B] mt-0.5 leading-relaxed">{blurb}</p>
                    </div>
                    {isSelected && (
                      <Check
                        size={18}
                        weight="bold"
                        className="text-ink flex-shrink-0 mt-1"
                        data-testid={`persona-selected-${key}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </SectionCard>

          <BreedAlertsCard />
          <KennelDefaultContractLinkCard />
          {/* Always-visible privacy/deletion entry point — App Store
              5.1.1(v) requires account deletion to be easy to find, and
              the "Privacy & account" tab scrolls off-screen on phones. */}
          <div
            className="bg-white border border-[#EAEAEA] rounded-xl p-5 shadow-luxury-light flex flex-wrap items-center justify-between gap-3"
            data-testid="profile-privacy-shortcut"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck size={20} weight="duotone" className="text-gold flex-shrink-0" />
              <p className="text-sm text-[#3F3F3F]">
                <span className="font-semibold text-ink">Privacy &amp; data rights</span>
                {" "}— export your data or delete your account.
              </p>
            </div>
            <Link
              to="/settings/privacy"
              data-testid="profile-privacy-link"
              className="inline-flex items-center rounded-full border border-[#E5E5E5] hover:bg-[#FAFAFA] text-ink text-[13px] font-semibold px-4 h-9 transition-colors"
            >
              Manage / Delete account →
            </Link>
          </div>
        </TabsContent>

        {/* AVAILABILITY — persona-aware pause / vacation mode */}
        <TabsContent value="availability" className="space-y-6 mt-0">
          <AvailabilityTab />
        </TabsContent>

        {/* NOTIFICATIONS — what shows up in the inbox */}
        <TabsContent value="notifications" className="space-y-6 mt-0">
          <SectionCard
            icon={BellRinging}
            iconColor="text-crimson"
            title="Inbox notifications"
            blurb="Choose which kinds of inbox notifications you receive. Transactional confirmations (purchases, registrations, direct breeding interest) are always delivered."
          >
            {!prefs ? (
              <div className="text-[#6B6B6B]">Loading…</div>
            ) : (
              <div className="divide-y divide-[#EAEAEA] -mx-2">
                {ROWS.map(({ key, title, description, Icon }) => (
                  <div
                    key={key}
                    className="px-2 py-4 flex items-start gap-4"
                    data-testid={`pref-row-${key}`}
                  >
                    <div className="p-2.5 rounded-lg bg-crimson/5 flex-shrink-0">
                      <Icon size={18} weight="duotone" className="text-crimson" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="font-medium text-ink">{title}</h3>
                        <Switch
                          checked={prefs[key] !== false}
                          onCheckedChange={toggle(key)}
                          data-testid={`pref-toggle-${key}`}
                        />
                      </div>
                      <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">{description}</p>
                    </div>
                  </div>
                ))}
                <div className="px-2 pt-5 pb-1">
                  <h3 className="font-bold text-ink text-[15px]">Push notifications</h3>
                  <p className="text-sm text-[#6B6B6B] mt-0.5 leading-relaxed">
                    These control the buzz on your phone's lock screen (mobile app).
                    Alerts always remain available in your inbox either way.
                  </p>
                </div>
                {PUSH_ROWS.map(({ key, title, description, Icon }) => (
                  <div
                    key={key}
                    className="px-2 py-4 flex items-start gap-4"
                    data-testid={`pref-row-${key}`}
                  >
                    <div className="p-2.5 rounded-lg bg-ink/5 flex-shrink-0">
                      <Icon size={18} weight="duotone" className="text-ink" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="font-medium text-ink">{title}</h3>
                        <Switch
                          checked={prefs[key] !== false}
                          onCheckedChange={toggle(key)}
                          data-testid={`pref-toggle-${key}`}
                        />
                      </div>
                      <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <Button
                onClick={save}
                disabled={!prefs || saving}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="save-preferences-btn"
              >
                {saving ? "Saving…" : (
                  <>
                    <CheckCircle size={14} className="mr-1" weight="fill" /> Save preferences
                  </>
                )}
              </Button>
            </div>
          </SectionCard>

          {/* Admin-only: daily ops digest opt-in. The endpoint returns
              digestOptIn === null for non-admins, which keeps this card
              hidden from regular kennel users entirely. */}
          {digestOptIn !== null && (
            <SectionCard
              icon={BellRinging}
              iconColor="text-gold"
              title="Daily ops digest (admins)"
              blurb="Receive a once-daily 08:00 UTC email summarising new sign-ups, listings, escrow movement, in-flight verifications, and unacked anomalies. Discord posts the same digest regardless."
              testId="digest-opt-in-card"
            >
              <div className="flex items-start gap-4 py-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-ink">Email me the daily digest</h3>
                  <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
                    One concise email each morning. Safe to leave on; no other admin email is added by toggling this.
                  </p>
                </div>
                <Switch
                  checked={!!digestOptIn}
                  onCheckedChange={toggleDigestOptIn}
                  disabled={digestSaving}
                  data-testid="digest-opt-in-toggle"
                />
              </div>
            </SectionCard>
          )}

          <LifecycleEmailPreferences />

          <HelpAndOnboarding replay={replay} />
        </TabsContent>

        {/* SECURITY — password + 2FA */}
        <TabsContent value="security" className="space-y-6 mt-0">
          <div>
            <h2 className="font-spectral text-2xl tracking-tight mb-1">Account security</h2>
            <p className="text-[13px] text-[#6B6B6B] mb-4 leading-relaxed">
              Lock down high-risk operations on your account.
            </p>
            <div className="gold-hairline mb-5 max-w-md" />
            <ChangePasswordCard />
            <div className="mt-6">
              <TwoFactorSettings />
            </div>
            <div className="mt-6">
              <BlockedUsersCard />
            </div>
          </div>
        </TabsContent>

        {/* LEGAL — executed agreements (downloadable PDF receipts) */}
        <TabsContent value="legal" className="space-y-6 mt-0">
          <ExecutedAgreementsCard />
          <AcknowledgementsCard />
        </TabsContent>

        {/* PRIVACY & ACCOUNT — data rights first (App Store 5.1.1(v)
            requires account deletion to be easy to find), then the
            deactivate card with extra friction below. */}
        <TabsContent value="danger" className="mt-0">
          <SectionCard
            icon={ShieldCheck}
            iconColor="text-gold"
            title="Privacy & data rights"
            blurb="Download every record we hold about you, or permanently delete your account."
            testId="privacy-link-card"
          >
            <div className="flex flex-wrap gap-3">
              <Link
                to="/settings/privacy"
                data-testid="privacy-link"
                className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 h-11 transition-colors"
              >
                Manage my data / Delete my account →
              </Link>
            </div>
            <SharingConsentToggle />
          </SectionCard>
          <div className="mt-6">
            <DangerZone />
          </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

/**
 * SharingConsentToggle — controls whether the member's Dog's Voice posts
 * can be shared publicly (share button + public /p/ pages).
 */
function SharingConsentToggle() {
  const [allow, setAllow] = useState(true);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/posts/sharing-consent")
      .then((r) => setAllow(r.data.allow_post_sharing !== false))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);
  const change = async (next) => {
    setAllow(next);
    try {
      await api.put("/posts/sharing-consent", { allow_post_sharing: next });
      toast.success(next
        ? "Public sharing enabled — your posts can be shared with a link."
        : "Public sharing off — your posts are members-only now.");
    } catch {
      setAllow(!next);
      toast.error("Could not save — try again.");
    }
  };
  if (!loaded) return null;
  return (
    <div className="mt-5 pt-5 border-t border-black/10 flex items-start gap-4" data-testid="sharing-consent-row">
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-ink">Allow public sharing of my posts</h3>
        <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
          When on, your Dog's Voice posts can be shared with a public link.
          Turn off to make your posts visible to registry members only —
          share buttons disappear and existing public links stop working.
        </p>
      </div>
      <Switch checked={allow} onCheckedChange={change} data-testid="sharing-consent-toggle" />
    </div>
  );
}

/**
 * NativeQuickActions — native-app-only card above the settings tabs.
 * Two jobs: (1) make account deletion impossible to miss (App Review
 * 5.1.1(v)), (2) contextual push-notification opt-in (4.2 native
 * functionality with a user-initiated permission prompt).
 */
function NativeQuickActions() {
  const [pushState, setPushState] = useState("idle"); // idle | busy | on | denied

  const enablePush = async () => {
    haptic("light");
    setPushState("busy");
    const ok = await registerPushNotifications(api);
    if (ok) {
      setPushState("on");
      toast.success("Notifications enabled on this device.");
    } else {
      setPushState("denied");
      toast.message("Notifications are off. You can enable them in iOS Settings → Armstrong K9 Registry.");
    }
  };

  return (
    <div className="mb-8 rounded-2xl border border-[#EAEAEA] bg-white divide-y divide-[#F0F0F0]" data-testid="native-quick-actions">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-start gap-3 min-w-0">
          <BellRinging size={20} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-ink">Push notifications</p>
            <p className="text-[12px] text-[#6B6B6B]">New inquiries, messages, and reminders on this device.</p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={enablePush}
          disabled={pushState === "busy" || pushState === "on"}
          className="rounded-full bg-ink hover:bg-crimson text-white text-[12px] h-9 px-4 flex-shrink-0"
          data-testid="native-enable-push-btn"
        >
          {pushState === "on" ? "Enabled" : pushState === "busy" ? "Enabling…" : "Enable"}
        </Button>
      </div>
      <Link
        to="/settings/privacy"
        className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-[#FAFAFA] transition-colors"
        data-testid="native-manage-data-link"
      >
        <div className="flex items-start gap-3 min-w-0">
          <ShieldCheck size={20} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-ink">Manage my data / Delete my account</p>
            <p className="text-[12px] text-[#6B6B6B]">Download your records or permanently delete your account.</p>
          </div>
        </div>
        <span className="text-[#9A9A9A] flex-shrink-0">→</span>
      </Link>
    </div>
  );
}

/**
 * Standardised settings panel. Gold-hairline divider under the title
 * matches the rest of the platform; the icon tile and copy follow the
 * same spacing rhythm as Dashboard / Marketplace LuxuryCards.
 */

/**
 * ExecutedAgreementsCard — Settings → Legal tab.
 *
 * Lists every click-wrap acceptance on file for the calling user
 * (Terms of Service, Privacy Policy, Storefront Seller Agreement,
 * Transporter Agreement) and offers a downloadable PDF receipt for
 * each one. The receipt has a personalized cover page (legal name,
 * email, acceptance timestamp, IP, version) followed by the full
 * agreement body — the same artifact admins can pull from the user
 * dialog, so the user always sees the same record we have.
 */
function ExecutedAgreementsCard() {
  const [consents, setConsents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/consents")
      .then((r) => { if (!cancelled) setConsents(r.data?.consents || []); })
      .catch(() => { if (!cancelled) setConsents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const download = async (c) => {
    setDownloadingId(c.kind + (c.download_query || ""));
    try {
      const name = await downloadAuthenticatedPdf(
        api,
        `/me/consents/executed.pdf?${c.download_query}`,
        "executed-agreement.pdf",
      );
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't download executed PDF."));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <SectionCard
      icon={BookOpen}
      iconColor="text-gold-dark"
      title="Your executed agreements"
      blurb="Every legal agreement you've accepted on Armstrong K9 Registry. Download a personalized PDF receipt — your name, the timestamp, the IP address you accepted from, and the full text of the agreement — anytime, for your own records or a dispute."
      testId="executed-agreements-card"
    >
      {loading && (
        <p className="text-sm text-[#6B6B6B]">Loading…</p>
      )}
      {!loading && consents && consents.length === 0 && (
        <p className="text-sm text-[#6B6B6B] leading-relaxed">
          You haven't accepted any agreements on file yet. New acceptances
          show up here as soon as they're recorded.
        </p>
      )}
      {!loading && consents && consents.length > 0 && (
        <ul className="divide-y divide-[#EEE]" data-testid="executed-agreements-list">
          {consents.map((c) => {
            const rowKey = c.kind + (c.download_query || "");
            const busy = downloadingId === rowKey;
            return (
              <li
                key={rowKey}
                className="py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
                data-testid={`executed-agreement-row-${c.kind}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-spectral text-lg tracking-tight text-ink leading-tight">
                    {c.doc}
                  </p>
                  <p className="text-xs text-[#6B6B6B] mt-1">
                    Accepted {new Date(c.accepted_at).toLocaleString()} ·
                    {" "}version <span className="font-tabular">{c.version}</span>
                    {c.ip && (
                      <> · IP <span className="font-mono">{c.ip}</span></>
                    )}
                  </p>
                  <p className="text-[11px] text-[#6B6B6B] mt-0.5">
                    {c.source}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full border-[#E5E5E5] h-9 px-4 text-[12px] flex-shrink-0"
                  onClick={() => download(c)}
                  disabled={busy}
                  data-testid={`download-executed-${c.kind}`}
                >
                  {busy ? "Preparing…" : "Download PDF"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[11px] text-[#6B6B6B] mt-4 leading-relaxed">
        These receipts are the same artifacts an Armstrong K9 admin would
        produce if a court, regulator, or insurance carrier requested
        proof of acceptance. They are not a substitute for the
        click-wrap acceptance itself — click-wrap is enforceable under
        U.S. federal case law — but they are the canonical evidence
        for it.
      </p>
    </SectionCard>
  );
}


/**
 * AcknowledgementsCard — Settings → Legal tab (below executed agreements).
 * Non-signature consent records: venue acknowledgement, per-dog buyer
 * acknowledgements, international seller agreement, and the cookie /
 * marketing preference event history.
 */
const PREF_KIND_LABEL = {
  cookies: "Cookie preferences",
  marketing_emails: "Marketing emails",
  buyer_pass: "Buyer Pass consent",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
};

function AcknowledgementsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/acknowledgements")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const venue = data?.venue_ack;
  const buyerAcks = data?.buyer_acks || [];
  const intl = data?.intl_seller_agreement;
  const events = data?.preference_events || [];
  const empty = !venue && buyerAcks.length === 0 && !intl && events.length === 0;

  return (
    <SectionCard
      icon={ShieldCheck}
      iconColor="text-gold-dark"
      title="Acknowledgements & preferences"
      blurb="Everything else you've consented to — the venue acknowledgement, per-dog purchase acknowledgements, and your cookie and marketing choices — with the exact time each was recorded."
      testId="acknowledgements-card"
    >
      {loading && <p className="text-sm text-[#6B6B6B]">Loading…</p>}
      {!loading && empty && (
        <p className="text-sm text-[#6B6B6B] leading-relaxed" data-testid="acknowledgements-empty">
          Nothing on file yet. Acknowledgements appear here the moment
          they're recorded — for example when you inquire about a dog or
          choose your cookie preferences.
        </p>
      )}
      {!loading && venue && (
        <div className="py-3 border-b border-[#EEE]" data-testid="ack-venue-row">
          <p className="font-spectral text-lg tracking-tight text-ink leading-tight">
            Venue Acknowledgement
          </p>
          <p className="text-xs text-[#6B6B6B] mt-1">
            Acknowledged {new Date(venue.acknowledged_at).toLocaleString()} ·
            {" "}version <span className="font-tabular">{venue.version || "—"}</span>
            {venue.ip && <> · IP <span className="font-mono">{venue.ip}</span></>}
          </p>
          {(venue.venue_text_snapshot || venue.at_own_risk_snapshot) && (
            <details className="mt-2">
              <summary className="text-xs text-gold-dark cursor-pointer" data-testid="ack-venue-toggle">
                View the exact text you acknowledged
              </summary>
              <div className="mt-2 text-xs text-[#3F3F3F] leading-relaxed whitespace-pre-wrap bg-[#FAFAFA] border border-[#EEE] rounded-lg p-3">
                {[venue.venue_text_snapshot, venue.at_own_risk_snapshot].filter(Boolean).join("\n\n")}
              </div>
            </details>
          )}
        </div>
      )}
      {!loading && intl && (
        <div className="py-3 border-b border-[#EEE]" data-testid="ack-intl-row">
          <p className="font-spectral text-lg tracking-tight text-ink leading-tight">
            International Seller Agreement
          </p>
          <p className="text-xs text-[#6B6B6B] mt-1">
            Accepted {new Date(intl.accepted_at).toLocaleString()} ·
            {" "}version <span className="font-tabular">{intl.version || "—"}</span>
            {intl.ip && <> · IP <span className="font-mono">{intl.ip}</span></>}
          </p>
          {intl.agreement_text && (
            <details className="mt-2">
              <summary className="text-xs text-gold-dark cursor-pointer">
                View the agreement text you accepted
              </summary>
              <div className="mt-2 text-xs text-[#3F3F3F] leading-relaxed whitespace-pre-wrap bg-[#FAFAFA] border border-[#EEE] rounded-lg p-3">
                {intl.agreement_text}
              </div>
            </details>
          )}
        </div>
      )}
      {!loading && buyerAcks.length > 0 && (
        <div className="py-3 border-b border-[#EEE]" data-testid="ack-buyer-list">
          <p className="font-spectral text-lg tracking-tight text-ink leading-tight">
            Per-dog buyer acknowledgements
          </p>
          <ul className="mt-2 space-y-2">
            {buyerAcks.map((b, i) => (
              <li key={i} className="text-xs text-[#6B6B6B]" data-testid={`ack-buyer-row-${i}`}>
                <span className="text-ink font-semibold">
                  {b.dog_name || b.breed || "Dog listing"}
                </span>
                {b.breed && b.dog_name && <> · {b.breed}</>}
                {" "}— acknowledged {new Date(b.acknowledged_at).toLocaleString()}
                {b.surface && <> · via {b.surface}</>}
                {b.breed_warning && (
                  <span className="block mt-0.5 text-[11px] italic">
                    Included breed advisory: {b.breed_warning}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!loading && events.length > 0 && (
        <div className="py-3" data-testid="ack-preferences-list">
          <p className="font-spectral text-lg tracking-tight text-ink leading-tight">
            Preference history
          </p>
          <ul className="mt-2 space-y-1.5">
            {events.map((e, i) => (
              <li key={i} className="text-xs text-[#6B6B6B]" data-testid={`ack-pref-row-${i}`}>
                <span className="text-ink font-semibold">
                  {PREF_KIND_LABEL[e.kind] || e.kind}
                </span>
                {" "}— <span className="font-tabular">{e.value}</span>
                {" "}· {new Date(e.created_at).toLocaleString()}
                {e.origin && <> · from {e.origin}</>}
                {e.document_version && <> · version {e.document_version}</>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11px] text-[#6B6B6B] mt-4 leading-relaxed">
        These are event-style records, not signed agreements — the signed
        agreements with downloadable PDF receipts are in the card above.
      </p>
    </SectionCard>
  );
}


function SectionCard({ icon: Icon, iconColor = "text-ink", title, blurb, children, testId }) {
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl shadow-luxury-light overflow-hidden"
      data-testid={testId}
    >
      <div className="px-6 py-5 flex items-center gap-3">
        <Icon size={22} weight="duotone" className={iconColor} />
        <h2 className="font-spectral text-2xl tracking-tight">{title}</h2>
      </div>
      <div className="gold-hairline mx-6" />
      <div className="px-6 py-5">
        {blurb && (
          <p className="text-sm text-[#6B6B6B] mb-4 max-w-2xl leading-relaxed">{blurb}</p>
        )}
        {children}
      </div>
    </div>
  );
}

function HelpAndOnboarding({ replay }) {
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 shadow-luxury-light"
      data-testid="help-onboarding-card"
    >
      <div className="flex-shrink-0 p-3 rounded-lg bg-cream ring-1 ring-[#E2DAC2]">
        <BookOpen size={22} weight="duotone" className="text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-spectral text-lg tracking-tight text-ink">Help & onboarding</h3>
        <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
          Re-run the 8-step product tour or open the full getting-started guide.
        </p>
      </div>
      <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
        <Button type="button" variant="outline" asChild className="rounded-full border-[#E2DAC2] hover:border-gold/55 hover:bg-cream">
          <Link to="/help" data-testid="settings-help-link">
            <BookOpen size={14} className="mr-1.5" /> Open guide
          </Link>
        </Button>
        <Button
          type="button"
          onClick={replay}
          className="rounded-full bg-ink hover:bg-crimson text-white"
          data-testid="settings-replay-tour-btn"
        >
          <PlayCircle size={14} className="mr-1.5" weight="duotone" /> Replay tour
        </Button>
      </div>
    </div>
  );
}

function DangerZone() {
  const [exporting, setExporting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/me/export.json");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `armstrong-registry-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const deactivate = async () => {
    if (confirmText !== "DEACTIVATE") return;
    setDeactivating(true);
    try {
      await api.post("/me/deactivate", { confirm: "DEACTIVATE" });
      toast.success("Account deactivated. You'll be logged out.");
      window.location.href = "/login";
    } catch (err) {
      toast.error(formatApiError(err));
      setDeactivating(false);
    }
  };

  return (
    <div className="bg-white border border-red-200 rounded-xl p-6 shadow-luxury-light" data-testid="danger-zone-card">
      <div className="flex items-center gap-3 mb-1">
        <Warning size={22} weight="duotone" className="text-red-700" />
        <h2 className="font-spectral text-2xl tracking-tight text-red-700">Danger zone</h2>
      </div>
      <p className="text-sm text-[#6B6B6B] mb-5 max-w-2xl leading-relaxed">
        Export everything we have on you, or deactivate your account. Deactivation prevents login but retains records (transfers, sales) for legal/audit purposes.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={exportData}
          disabled={exporting}
          variant="outline"
          className="rounded-full border-[#E5E5E5]"
          data-testid="export-my-data-btn"
        >
          {exporting ? "Exporting…" : "Export my data (JSON)"}
        </Button>
        {!showConfirm ? (
          <Button
            onClick={() => setShowConfirm(true)}
            variant="outline"
            className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
            data-testid="deactivate-account-btn"
          >
            Deactivate account
          </Button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center bg-red-50 border border-red-200 rounded-lg p-3 flex-1">
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder='Type "DEACTIVATE" to confirm'
              className="flex-1 px-3 py-2 border border-red-200 rounded text-sm"
              data-testid="deactivate-confirm-input"
            />
            <Button
              onClick={deactivate}
              disabled={confirmText !== "DEACTIVATE" || deactivating}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="deactivate-confirm-btn"
            >
              {deactivating ? "Deactivating…" : "Confirm"}
            </Button>
            <Button onClick={() => { setShowConfirm(false); setConfirmText(""); }} variant="outline" size="sm" className="rounded-full">Cancel</Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Change-password card — authenticated, requires current password to
 * defend against stolen-token quietly rotating creds out from under
 * the real owner. Lives in the Security tab above 2FA.
 */
function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  // null = checking; true = has a password (change mode); false = Google-only (set mode)
  const [hasPassword, setHasPassword] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setHasPassword(r.data?.has_password !== false))
      .catch(() => setHasPassword(true)); // default to the safe change-mode
  }, []);

  const setMode = hasPassword === false;
  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const submitDisabled =
    busy || (!setMode && !current) || !next || !confirm || mismatch || tooShort;

  const submit = async () => {
    if (submitDisabled) return;
    setBusy(true);
    setOk(false);
    try {
      if (setMode) {
        await api.post("/auth/me/set-password", { new_password: next });
        setHasPassword(true);
        toast.success("Password set. You can now also sign in with email + password.");
      } else {
        await api.post("/auth/me/change-password", {
          current_password: current,
          new_password: next,
        });
        toast.success("Password updated.");
      }
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-6"
      data-testid="change-password-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-ink/5 border border-[#EAEAEA] rounded-lg p-2.5 flex-shrink-0">
          <Key size={20} weight="duotone" className="text-ink" />
        </div>
        <div>
          <h3 className="font-spectral text-lg tracking-tight">
            {setMode ? "Set a password" : "Change password"}
          </h3>
          <p className="text-[13px] text-[#6B6B6B] mt-0.5 leading-relaxed">
            {setMode
              ? "You signed up with Google, so this account has no password yet. Add one to also sign in with email + password — a handy backup if you ever lose access to Google."
              : "At least 8 characters. We'll sign your other sessions out the next time they refresh."}
          </p>
        </div>
      </div>
      <div className="space-y-3 max-w-md">
        {!setMode && (
          <div>
            <Label htmlFor="cp-current" className="text-xs font-bold uppercase tracking-[0.15em]">
              Current password
            </Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1.5"
              data-testid="change-password-current"
            />
          </div>
        )}
        <div>
          <Label htmlFor="cp-new" className="text-xs font-bold uppercase tracking-[0.15em]">
            New password
          </Label>
          <Input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="mt-1.5"
            data-testid="change-password-new"
          />
          {tooShort && (
            <p className="text-xs text-crimson mt-1">Use at least 8 characters.</p>
          )}
        </div>
        <div>
          <Label htmlFor="cp-confirm" className="text-xs font-bold uppercase tracking-[0.15em]">
            Confirm new password
          </Label>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1.5"
            data-testid="change-password-confirm"
          />
          {mismatch && (
            <p className="text-xs text-crimson mt-1">Passwords don't match.</p>
          )}
        </div>
        <div className="flex items-center gap-3 pt-1">
          <Button
            onClick={submit}
            disabled={submitDisabled}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid={setMode ? "set-password-submit" : "change-password-submit"}
          >
            {busy
              ? setMode ? "Setting…" : "Updating…"
              : setMode ? "Set password" : "Update password"}
          </Button>
          {ok && (
            <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="change-password-ok">
              <CheckCircle size={16} weight="fill" />
              {setMode ? "Saved" : "Updated"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Lifecycle email preferences — CAN-SPAM-compliant global opt-outs
 * for the date-trigger lifecycle emails (birthday, gotcha day,
 * day-30 buyer follow-up, puppy school drip).
 *
 * Backend: /api/me/email-preferences. Independent of the in-app inbox
 * notification preferences above (those govern bell notifications
 * only). Toggling these to off-state silences the Resend send only;
 * inbox bell notifications still fire because they're free, opt-in,
 * and useful even for users who don't want email noise.
 */
function LifecycleEmailPreferences() {
  const [prefs, setPrefs] = React.useState(null);
  const [savingKey, setSavingKey] = React.useState(null);

  React.useEffect(() => {
    api.get("/me/email-preferences")
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({
        lifecycle_unsubscribed: false,
        puppy_school_unsubscribed: false,
        inquiry_alerts_unsubscribed: false,
        thread_nudges_unsubscribed: false,
      }));
  }, []);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setSavingKey(key);
    setPrefs(next); // optimistic
    try {
      const { data } = await api.put("/me/email-preferences", { [key]: next[key] });
      setPrefs(data);
      toast.success("Email preferences updated.");
    } catch (e) {
      setPrefs(prefs); // revert
      toast.error(formatApiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  if (!prefs) return null;

  return (
    <SectionCard
      icon={EnvelopeSimple}
      iconColor="text-gold"
      title="Email preferences"
      blurb="Choose which email categories you receive. Transactional emails (purchases, password resets, paid breeding inquiries with refund timers) always send."
      testId="lifecycle-email-prefs-card"
    >
      <div className="space-y-3">
        <div className="flex items-start gap-4 py-2" data-testid="inquiry-alerts-email-row">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-ink">Marketplace inquiry alerts</h3>
            <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
              An email when a buyer taps "Show interest" on one of your listings — includes their
              contact info so you can reply straight from your inbox. You'll still get the in-app
              notification either way.
            </p>
          </div>
          <Switch
            checked={!prefs.inquiry_alerts_unsubscribed}
            onCheckedChange={() => toggle("inquiry_alerts_unsubscribed")}
            disabled={savingKey === "inquiry_alerts_unsubscribed"}
            data-testid="toggle-inquiry-alerts-unsubscribed"
          />
        </div>
        <div className="gold-hairline" />
        <div className="flex items-start gap-4 py-2" data-testid="thread-nudges-email-row">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-ink">Unread message nudges</h3>
            <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
              A reminder email when a provider/client message has sat unread for 24 hours —
              one nudge per conversation until you read it.
            </p>
          </div>
          <Switch
            checked={!prefs.thread_nudges_unsubscribed}
            onCheckedChange={() => toggle("thread_nudges_unsubscribed")}
            disabled={savingKey === "thread_nudges_unsubscribed"}
            data-testid="toggle-thread-nudges-unsubscribed"
          />
        </div>
        <div className="gold-hairline" />
        <div className="flex items-start gap-4 py-2" data-testid="lifecycle-email-row">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-ink">Annual milestones + buyer check-in</h3>
            <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
              Birthday and Gotcha Day each year, plus a one-time 30-day "how's it going" email after a transfer.
              Transactional confirmations (purchases, password resets, transfer requests) will still send.
            </p>
          </div>
          <Switch
            checked={!prefs.lifecycle_unsubscribed}
            onCheckedChange={() => toggle("lifecycle_unsubscribed")}
            disabled={savingKey === "lifecycle_unsubscribed"}
            data-testid="toggle-lifecycle-unsubscribed"
          />
        </div>
        <div className="gold-hairline" />
        <div className="flex items-start gap-4 py-2" data-testid="puppy-school-email-row">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-ink">First-puppy school drip</h3>
            <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
              Six short lessons on day 1, 3, 5, 7, 14, and 30 after homecoming — homecoming basics, feeding rhythm,
              crate confidence, first-week recap, socialization, and a day-30 review nudge. Educational only, not
              veterinary advice.
            </p>
          </div>
          <Switch
            checked={!prefs.puppy_school_unsubscribed}
            onCheckedChange={() => toggle("puppy_school_unsubscribed")}
            disabled={savingKey === "puppy_school_unsubscribed"}
            data-testid="toggle-puppy-school-unsubscribed"
          />
        </div>
        <p className="text-[11px] text-[#9A9A9A] pt-1 leading-relaxed">
          Per-dog opt-outs live on each dog's <strong>Ownership</strong> tab if you want to mute these for one
          specific dog only — e.g., adult rescue where puppy school doesn't apply.
        </p>
      </div>
    </SectionCard>
  );
}

